import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { Skeleton } from '@/components/atoms/skeleton';
import { DetailPageLayout } from '@/components/layouts';
import { SettingsPageHeader } from '@/components/layouts/SettingsPageHeader';
import { SchedulingSettingsOverview } from '@/components/organisms/settings-scheduling/SchedulingSettingsOverview';
import { useBusinessSettings } from '@/hooks/settings/useBusinessSettings';
import { useHasPermission } from '@/hooks/usePermissions';

export const Route = createFileRoute('/_dashboard/dashboard/settings/office-settings')({
  component: SchedulingSettingsLayout,
});

function SchedulingSettingsLayout() {
  const matches = useMatches();
  const isLeaf =
    matches[matches.length - 1]?.routeId === '/_dashboard/dashboard/settings/office-settings';

  if (!isLeaf) return <Outlet />;

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-none border-0 bg-card shadow-none md:rounded-b-xl md:border-x md:border-b md:shadow-sm">
      <SchedulingSettingsPage />
    </div>
  );
}

function SchedulingSettingsPage() {
  const { data: settings, isLoading } = useBusinessSettings();
  const canEdit = useHasPermission('business_hours:modify');

  if (isLoading) {
    return (
      <DetailPageLayout
        header={<SettingsPageHeader title="Scheduling" />}
        headerClassName="min-h-[57px] px-4 py-0 sm:px-6"
        contentClassName="bg-[#f4f4f1] p-0"
      >
        <div className="mx-auto w-full max-w-[1600px] space-y-4 px-3 py-4 sm:px-6 sm:py-5">
          <Skeleton className="h-56 w-full rounded-xl" />
          <div className="grid gap-4 sm:grid-cols-2">
            <Skeleton className="h-44 w-full rounded-xl" />
            <Skeleton className="h-44 w-full rounded-xl" />
          </div>
          <Skeleton className="h-32 w-full rounded-xl" />
        </div>
      </DetailPageLayout>
    );
  }

  if (!settings) {
    return (
      <DetailPageLayout
        header={<SettingsPageHeader title="Scheduling" />}
        headerClassName="min-h-[57px] px-4 py-0 sm:px-6"
        contentClassName="bg-[#f4f4f1] p-0"
      >
        <div className="mx-auto w-full max-w-[1600px] px-3 py-4 sm:px-6 sm:py-5">
          <div className="rounded-lg border bg-background p-6 text-sm text-muted-foreground">
            Scheduling settings are not available.
          </div>
        </div>
      </DetailPageLayout>
    );
  }

  return <SchedulingSettingsOverview settings={settings} canEdit={canEdit} />;
}
